import {
  Table,
  Thead,
  Td,
  Th,
  Tr,
  Tbody,
  Box,
  Flex,
  Tfoot,
  Heading,
} from '@chakra-ui/react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  SortingState,
  Row,
  HeaderGroup,
  SortDirection,
} from '@tanstack/react-table';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { FacilitiesColumnSelector } from './FacilitiesColumnSelector';
import {
  FACILITIES_TABLE_CONFIG,
  FacilityTableColumnKey,
  FacilitiesTableRow,
} from './facilitiesTableOptions';
import { CreditFacility } from '~/contexts/CreditFacilitiesProvider/useCreditFacilities';
import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';
import { useCreditFacilityContext } from '~/packages/credit-facility/hooks/CreditFacilityProvider';

// Constants
const FIXED_FACILITIES_TABLE_COLUMN_STYLES = {
  left: 0,
  zIndex: 2,
  borderRightWidth: 'medium',
  borderRightColor: colors.gray[100],
} as const;

// Fragments
type FacilitiesTableCellProps = {
  children: React.ReactNode;
  enableFacilitySelection?: boolean;
  fixed?: boolean;
  onClick: () => void;
};
const FacilitiesTableCell = ({
  children,
  enableFacilitySelection = false,
  fixed = false,
  onClick,
}: FacilitiesTableCellProps) => {
  const fixedProps = {
    _hover: enableFacilitySelection
      ? { backgroundColor: colors.gray[50], textDecoration: 'underline' }
      : {},
    cursor: enableFacilitySelection ? 'pointer' : 'default',
    onClick: enableFacilitySelection ? onClick : () => {},
    position: 'sticky',
    style: FIXED_FACILITIES_TABLE_COLUMN_STYLES,
  } as const;
  return (
    <Td
      backgroundColor="white"
      color={colors.black[70]}
      fontSize="1rem"
      maxWidth="14rem"
      minWidth="14rem"
      padding="1rem"
      verticalAlign="top"
      width="14rem"
      {...(fixed ? fixedProps : {})}
    >
      <Flex
        alignItems="center"
        color={
          fixed && enableFacilitySelection ? colors.blue[400] : colors.black[95]
        }
        gap="0.5rem"
      >
        {children}
      </Flex>
    </Td>
  );
};

type FacilitiesTableRowProps = {
  enableFacilitySelection?: boolean;
  row: Row<FacilitiesTableRow>;
  onClick: () => void;
};
const FacilitiesTableRowComponent = ({
  enableFacilitySelection = false,
  row,
  onClick,
}: FacilitiesTableRowProps) => (
  <Tr>
    {row.getVisibleCells().map((cell) => {
      const columnId = cell.column.id as FacilityTableColumnKey;
      return (
        <FacilitiesTableCell
          key={cell.id}
          fixed={FACILITIES_TABLE_CONFIG[columnId].fixed}
          enableFacilitySelection={enableFacilitySelection}
          onClick={() => onClick()}
        >
          {flexRender(
            FACILITIES_TABLE_CONFIG[columnId].parse(cell.getValue()),
            cell.getContext(),
          )}
        </FacilitiesTableCell>
      );
    })}
  </Tr>
);

type FacilitiesTableHeaderCellProps = {
  children: React.ReactNode;
  columnCanSort: boolean;
  fixed?: boolean;
  onClick?: (event: unknown) => void;
};
const FacilitiesTableHeaderCell = ({
  children,
  columnCanSort,
  fixed = false,
  onClick = () => {},
}: FacilitiesTableHeaderCellProps) => (
  <Th
    backgroundColor={colors.background[200]}
    color={colors.black[95]}
    cursor={columnCanSort ? 'pointer' : 'default'}
    fontSize="1rem"
    fontWeight={500}
    lineHeight="1.5rem"
    maxWidth="14rem"
    minWidth="14rem"
    padding="1rem"
    position="sticky"
    verticalAlign="top"
    width="14rem"
    zIndex={1}
    style={fixed ? FIXED_FACILITIES_TABLE_COLUMN_STYLES : undefined}
    onClick={onClick}
  >
    <Flex alignItems="baseline" justifyContent="space-between" gap="0.5rem">
      {children}
    </Flex>
  </Th>
);

type SortIconProps = { columnCanSort: boolean; sorted: false | SortDirection };
const SortIcon = ({ columnCanSort, sorted }: SortIconProps) => {
  if (sorted === 'asc') return <Icon icon="sort-up" />;
  if (sorted === 'desc') return <Icon icon="sort-down" />;
  if (columnCanSort) return <Icon icon="sort" />;
  return null;
};
type FacilitiesTableHeaderRowProps = {
  headers: HeaderGroup<FacilitiesTableRow>['headers'];
};
const FacilitiesTableHeaderRow = ({
  headers,
}: FacilitiesTableHeaderRowProps) => (
  <Tr>
    {headers.map((header) => {
      const columnCanSort = header.column.getCanSort();
      const handleSortClick = header.column.getToggleSortingHandler();
      const columnId = header.column.id as FacilityTableColumnKey;
      return (
        <FacilitiesTableHeaderCell
          key={header.id}
          fixed={FACILITIES_TABLE_CONFIG[columnId].fixed}
          onClick={handleSortClick}
          columnCanSort={columnCanSort}
        >
          {flexRender(header.column.columnDef.header, header.getContext())}
          <SortIcon
            columnCanSort={columnCanSort}
            sorted={header.column.getIsSorted()}
          />
        </FacilitiesTableHeaderCell>
      );
    })}
  </Tr>
);

type FacilitiesTableFooterCellProps = {
  children: React.ReactNode;
  fixed?: boolean;
};
const FacilitiesTableFooterCell = ({
  children,
  fixed = false,
}: FacilitiesTableFooterCellProps) => (
  <Th
    backgroundColor={colors.gray[100]}
    color={colors.black[95]}
    fontSize="1rem"
    fontWeight={500}
    lineHeight="1.5rem"
    maxWidth="14rem"
    minWidth="14rem"
    padding="1rem"
    position="sticky"
    verticalAlign="top"
    width="14rem"
    style={fixed ? FIXED_FACILITIES_TABLE_COLUMN_STYLES : undefined}
    zIndex={1}
  >
    <Flex alignItems="center" gap="0.5rem">
      {children}
    </Flex>
  </Th>
);
type FacilitiesTableFooterRowProps = {
  headers: HeaderGroup<FacilitiesTableRow>['headers'];
  totalsRow: FacilitiesTableRow;
};
const FacilitiesTableFooterRow = ({
  headers,
  totalsRow,
}: FacilitiesTableFooterRowProps) => (
  <Tr>
    {headers.map((header) => {
      const columnId = header.column.id as FacilityTableColumnKey;
      return (
        <FacilitiesTableFooterCell
          key={header.id}
          fixed={FACILITIES_TABLE_CONFIG[columnId].fixed}
        >
          {flexRender(
            FACILITIES_TABLE_CONFIG[columnId].parse(totalsRow[columnId]),
            header.getContext(),
          )}
        </FacilitiesTableFooterCell>
      );
    })}
  </Tr>
);

// Exports
type FacilitiesTableProps = {
  creditFacilities: CreditFacility[];
  enableFacilitySelection: boolean;
  rows: FacilitiesTableRow[];
  totalsRow: FacilitiesTableRow;
};
export const FacilitiesTable = ({
  creditFacilities,
  enableFacilitySelection,
  rows,
  totalsRow,
}: FacilitiesTableProps) => {
  const navigate = useNavigate();
  const { setCreditFacilityId } = useCreditFacilityContext();

  // Tanstack table column state mgmt
  const [columnVisibility, setColumnVisibility] = useState(() =>
    Object.fromEntries(
      Object.entries(FACILITIES_TABLE_CONFIG).map(([key]) => [key, true]),
    ),
  );
  const [sorting, setSorting] = useState<SortingState>([]);
  const table = useReactTable({
    data: rows,
    columns: Object.values(FACILITIES_TABLE_CONFIG).map((columnConfig) => ({
      accessorKey: columnConfig.accessorKey,
      header: columnConfig.header,
    })),
    state: { columnVisibility, sorting },
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
    debugHeaders: true,
    debugColumns: true,
  });

  return (
    <Flex flexDirection="column" gap="1.5rem">
      <Flex alignItems="center">
        <Heading
          as="h3"
          fontSize="1.5rem"
          fontWeight={600}
          lineHeight="2rem"
          flexGrow={1}
        >
          Facilities
        </Heading>
        <FacilitiesColumnSelector tableInstance={table} />
      </Flex>
      <Box
        borderWidth="thin"
        borderColor={colors.gray[100]}
        overflowX="auto"
        overflowY="auto"
        position="relative"
      >
        <Table style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <FacilitiesTableHeaderRow
                key={headerGroup.id}
                headers={headerGroup.headers}
              />
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <FacilitiesTableRowComponent
                key={row.id}
                row={row}
                enableFacilitySelection={enableFacilitySelection}
                onClick={() => {
                  const creditFacility = creditFacilities.find(
                    ({ formattedSlug }) =>
                      formattedSlug === row.getValue('creditFacilityName'),
                  );
                  if (creditFacility?.id) {
                    setCreditFacilityId(creditFacility.id);
                    navigate(
                      `/portfolio/summary?creditFacility=${creditFacility.id}`,
                    );
                  }
                }}
              />
            ))}
          </Tbody>
          <Tfoot>
            {table.getFooterGroups().map((footerGroup) => (
              <FacilitiesTableFooterRow
                key={footerGroup.id}
                headers={footerGroup.headers}
                totalsRow={totalsRow}
              />
            ))}
          </Tfoot>
        </Table>
      </Box>
    </Flex>
  );
};
